import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ConnectorService } from '../services/connector.service';
import * as connectorActions from './connector.actions';
import { catchError, filter, map, of, switchMap } from 'rxjs';
import { Store } from '@ngrx/store';

import { MessageTranslateService } from '../../../shared/services/message.service';
import {
  selectCurrentFilter,
  selectCurrentPagination,
} from './connector.selectors';
import { concatLatestFrom } from '@ngrx/operators';
import * as operatorsSelectors from '../../../store/operators/operators.selectors';
import * as operatorsActions from '../../../store/operators/operators.actions';
import { ConnectorRequestUtil } from '../utils/connector-request.util';
import { ChargerFilterService } from '../../chargers/services/charger-filter.service';
import { updateChardiStatusOnConnector } from '../../chargers/store/chargerOverviewStore/chargerOverview.actions';
import * as chargerConfigurationActions from '../../chargers/store/charger.configuration.store/charger.configuration.store.actions';
import * as chargerConfigurationSelectors from '../../chargers/store/charger.configuration.store/charger.configuration.store.selectors';

export class ConnectorEffects {
  actions$: Actions = inject(Actions);
  connectorService: ConnectorService = inject(ConnectorService);
  store: Store = inject(Store);
  messageService = inject(MessageTranslateService);
  chargerFilterService = inject(ChargerFilterService);

  uploadChargePoint$ = createEffect(() =>
    this.actions$.pipe(
      ofType(connectorActions.updateChargePoint),
      switchMap((requestData) =>
        this.connectorService
          .updateChargePoint(requestData.id, requestData.chargePoint)
          .pipe(
            map((res) => {
              if (!res.isSuccess) {
                return connectorActions.updateChargePointFailure({
                  error: res.error!,
                });
              } else {
                return connectorActions.updateChargerPointSuccess({
                  chargePoint: requestData.chargePoint,
                  id: requestData.id,
                });
              }
            })
          )
      )
    )
  );

  loadConnector$ = createEffect(() =>
    this.actions$.pipe(
      ofType(connectorActions.loadConnectorConfiguration),
      switchMap((requestData) =>
        this.connectorService.getConnectorConfiguration(requestData.id).pipe(
          map((res) => {
            if (res.isSuccess) {
              return connectorActions.loadConnectorConfigurationSuccess({
                connectorConfiguration: res.value!,
              });
            } else {
              return connectorActions.loadConnectorConfigurationFailure({
                error: res.error!,
              });
            }
          })
        )
      )
    )
  );

  loadConnectors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        connectorActions.loadConnectorList,
        connectorActions.changePaginationSuccessful,
        connectorActions.updateConnectorFilterSuccess,
        operatorsActions.SetCurrentOperatorSuccess
      ),
      concatLatestFrom(() => [
        this.store.select(operatorsSelectors.selectCurrentOperator),
        this.store.select(selectCurrentPagination),
        this.store.select(selectCurrentFilter),
      ]),
      switchMap(([_action, selectedOperator, pagination, currentFilter]) =>
        this.connectorService
          .searchConnectors(
            ConnectorRequestUtil.createConnectorListRequest(
              selectedOperator!.id!,
              pagination,
              currentFilter
            )
          )
          .pipe(
            map((res) => {
              if (res.isSuccess) {
                return connectorActions.loadConnectorListSuccess({
                  connectorResponse: res.value!,
                });
              } else {
                return connectorActions.loadConnectorListFailure({
                  error: res.error!,
                });
              }
            }),
            catchError((err) =>
              of(
                connectorActions.loadConnectorListFailure({
                  error: err.message || 'Loading connectors failed',
                })
              )
            )
          )
      )
    )
  );

  loadConnectorNumbersByStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        connectorActions.loadConnectorList,
        connectorActions.updateConnectorFilterSuccess,
        operatorsActions.SetCurrentOperatorSuccess,
        connectorActions.loadConnectorNumbersByStatus
      ),
      concatLatestFrom(() => [
        this.store.select(operatorsSelectors.selectCurrentOperator),
        this.store.select(selectCurrentPagination),
        this.store.select(selectCurrentFilter),
      ]),
      switchMap(([_action, selectedOperator, pagination, currentFilter]) =>
        this.connectorService
          .getConnectorNumbersByStatus(
            ConnectorRequestUtil.createConnectorListRequest(
              selectedOperator!.id!,
              pagination,
              currentFilter
            )
          )
          .pipe(
            map((res) => {
              if (res.isSuccess) {
                return connectorActions.loadConnectorNumbersByStatusSuccess({
                  value: res.value!,
                });
              } else {
                return connectorActions.loadConnectorNumbersByStatusFailure({
                  error: res.error!,
                });
              }
            }),
            catchError((err) =>
              of(
                connectorActions.loadConnectorNumbersByStatusFailure({
                  error:
                    err.message ||
                    'Loading connectors numbers by status failed',
                })
              )
            )
          )
      )
    )
  );

  changedPagination$ = createEffect(() =>
    this.actions$.pipe(
      ofType(connectorActions.changePagination),
      map((_) => connectorActions.changePaginationSuccessful())
    )
  );

  filterUpdated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(connectorActions.updateConnectorFilter),
      map((_) => connectorActions.updateConnectorFilterSuccess())
    )
  );

  updateChargerPointSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(connectorActions.updateChargerPointSuccess),
      map((data) => {
        const isChardi = data.chargePoint.isChardi ?? false;
        const id = data.id;
        return updateChardiStatusOnConnector({ connectorId: id, isChardi });
      })
    )
  );

  syncHasHpcConnectors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(connectorActions.updateChargerPointSuccess),
      concatLatestFrom(() =>
        this.store.select(chargerConfigurationSelectors.selectChargerId)
      ),
      filter(([, chargerId]) => chargerId != null),
      map(([, chargerId]) =>
        chargerConfigurationActions.loadChargerConfigurationDetails({
          chargerId: chargerId!,
          force: true,
        })
      )
    )
  );

  loadFilterParameters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(connectorActions.loadConnectorsFilterParameters),
      concatLatestFrom(() => [
        this.store.select(operatorsSelectors.selectCurrentOperator),
      ]),
      switchMap(([_action, operator]) =>
        this.connectorService.getFilterParameters(operator!.id!).pipe(
          map((res) => {
            if (res.isSuccess) {
              return connectorActions.loadConnectorsFilterParametersSuccess({
                filterParameters: res.value!,
              });
            } else {
              return connectorActions.loadConnectorsFilterParametersFailure({
                error: res.error!,
              });
            }
          }),
          catchError((err) =>
            of(
              connectorActions.loadConnectorsFilterParametersFailure({
                error: err.message || 'Load location filter parameters failed',
              })
            )
          )
        )
      )
    )
  );
}
